import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import About from "@/pages/About";
import FoodSafety from "@/pages/FoodSafety";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import MilletContent from "@/pages/MilletContent";
import Resources from "@/pages/Resources";
import NotFound from "@/pages/NotFound";
import { organizationSchema, websiteSchema, localBusinessSchema } from "@/lib/seo";

const GA4_ID = process.env.REACT_APP_GA4_ID;
const GSC_VERIFICATION = process.env.REACT_APP_GSC_VERIFICATION;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  return null;
}

/** Site-wide structured data (Helmet) + analytics/verification. */
function SiteHead() {
  useEffect(() => {
    // Analytics must be injected as real, executable script nodes.
    // (react-helmet-async renders inline scripts via innerHTML which does NOT execute.)
    if (typeof document === "undefined") return;

    if (GA4_ID && !document.getElementById("ga4-src")) {
      const s = document.createElement("script");
      s.id = "ga4-src";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
      document.head.appendChild(s);
      const cfg = document.createElement("script");
      cfg.id = "ga4-config";
      cfg.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA4_ID}');`;
      document.head.appendChild(cfg);
    }

    if (CLARITY_ID && !document.getElementById("ms-clarity")) {
      const c = document.createElement("script");
      c.id = "ms-clarity";
      c.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${CLARITY_ID}");`;
      document.head.appendChild(c);
    }
  }, []);

  return (
    <Helmet>
      {GSC_VERIFICATION && <meta name="google-site-verification" content={GSC_VERIFICATION} />}
      <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema())}</script>
    </Helmet>
  );
}

function PublicLayout({ children }) {
  return (
    <div className="grain">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollToTop />
          <SiteHead />
          <AuthProvider>
            <Toaster position="top-center" richColors />
            <Routes>
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/products" element={<PublicLayout><Products /></PublicLayout>} />
              <Route path="/products/:slug" element={<PublicLayout><ProductDetail /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/food-safety" element={<PublicLayout><FoodSafety /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/resources" element={<PublicLayout><Resources /></PublicLayout>} />
              <Route path="/millet-export" element={<PublicLayout><MilletContent pageKey="pillar" /></PublicLayout>} />
              <Route path="/millet-export/pearl-millet-bajra" element={<PublicLayout><MilletContent pageKey="pearl" /></PublicLayout>} />
              <Route path="/millet-export/green-millet" element={<PublicLayout><MilletContent pageKey="green" /></PublicLayout>} />
              <Route path="/millet-export/bulk-millet-supply" element={<PublicLayout><MilletContent pageKey="bulk" /></PublicLayout>} />
              <Route path="/millet-export/millet-for-human-consumption" element={<PublicLayout><MilletContent pageKey="human" /></PublicLayout>} />
              <Route path="/millet-export/millet-for-animal-feed" element={<PublicLayout><MilletContent pageKey="feed" /></PublicLayout>} />
              <Route path="/millet-export/uae" element={<PublicLayout><MilletContent pageKey="uae" /></PublicLayout>} />
              <Route path="/millet-export/regions" element={<PublicLayout><MilletContent pageKey="regions" /></PublicLayout>} />
              <Route path="/millet-export/regions/north-america" element={<PublicLayout><MilletContent pageKey="northAmerica" /></PublicLayout>} />
              <Route path="/millet-export/regions/europe" element={<PublicLayout><MilletContent pageKey="europe" /></PublicLayout>} />
              <Route path="/millet-export/regions/middle-east" element={<PublicLayout><MilletContent pageKey="middleEast" /></PublicLayout>} />
              <Route path="/millet-export/regions/asia-pacific" element={<PublicLayout><MilletContent pageKey="asiaPacific" /></PublicLayout>} />
              <Route path="/millet-export/regions/africa" element={<PublicLayout><MilletContent pageKey="africa" /></PublicLayout>} />
              <Route path="/resources/millet-export-guide" element={<PublicLayout><MilletContent pageKey="guide" /></PublicLayout>} />
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
